'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { DEFAULT_FESTIVAL_RADIUS_METER } from '@/constants/festival';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useSubscribedState } from '@/hooks/useSubscribedState';
import { parseEnumValue, parseNumberList, parseStringList, toggleArrayItem } from '../utils';
export type DonationBusinessSort = 'LATEST' | 'AMOUNT_DESC' | 'RATE_DESC' | 'SUPPORT_DESC' | 'DEADLINE_ASC';
const DONATION_SORT_OPTIONS: readonly DonationBusinessSort[] = ['LATEST', 'AMOUNT_DESC', 'RATE_DESC', 'SUPPORT_DESC', 'DEADLINE_ASC'];
export interface IntegratedSearchFilterState {
  regionCode: string | null;
  subRegionCode: string | null;
  policyInfoProvisionCodes: string[];
  giftCategoryIds: number[];
  giftInstitutionIds: number[];
  donationInstitutionIds: number[];
  donationSort: DonationBusinessSort;
  festivalLclsSystm2: string | null;
  festivalRegionCode: string | null;
  festivalSubRegionCode: string | null;
  festivalRadius: number;
  festivalMonth: number | null;
}
const DEFAULTS: IntegratedSearchFilterState = {
  regionCode: null,
  subRegionCode: null,
  policyInfoProvisionCodes: [],
  giftCategoryIds: [],
  giftInstitutionIds: [],
  donationInstitutionIds: [],
  donationSort: 'LATEST',
  festivalLclsSystm2: null,
  festivalRegionCode: null,
  festivalSubRegionCode: null,
  festivalRadius: DEFAULT_FESTIVAL_RADIUS_METER,
  festivalMonth: null
};
const PARAM_KEYS = {
  regionCode: 'region',
  subRegionCode: 'subRegion',
  policyInfoProvisionCodes: 'guideProvisions',
  giftCategoryIds: 'giftCategories',
  giftInstitutionIds: 'giftInstitutions',
  donationInstitutionIds: 'donationInstitutions',
  donationSort: 'donationSort',
  festivalLclsSystm2: 'festivalCategory',
  festivalRegionCode: 'festivalRegion',
  festivalSubRegionCode: 'festivalSubRegion',
  festivalRadius: 'festivalRadius',
  festivalMonth: 'festivalMonth',
  page: 'page'
} as const;
function parse(params: URLSearchParams): IntegratedSearchFilterState {
  return {
    regionCode: params.get(PARAM_KEYS.regionCode) || null,
    subRegionCode: params.get(PARAM_KEYS.subRegionCode) || null,
    policyInfoProvisionCodes: parseStringList(params.get(PARAM_KEYS.policyInfoProvisionCodes)),
    giftCategoryIds: parseNumberList(params.get(PARAM_KEYS.giftCategoryIds)),
    giftInstitutionIds: parseNumberList(params.get(PARAM_KEYS.giftInstitutionIds)),
    donationInstitutionIds: parseNumberList(params.get(PARAM_KEYS.donationInstitutionIds)),
    donationSort: parseEnumValue(params.get(PARAM_KEYS.donationSort), DONATION_SORT_OPTIONS, DEFAULTS.donationSort),
    festivalLclsSystm2: params.get(PARAM_KEYS.festivalLclsSystm2) || null,
    festivalRegionCode: params.get(PARAM_KEYS.festivalRegionCode) || null,
    festivalSubRegionCode: params.get(PARAM_KEYS.festivalSubRegionCode) || null,
    festivalRadius: Number(params.get(PARAM_KEYS.festivalRadius)) || DEFAULTS.festivalRadius,
    festivalMonth: Number(params.get(PARAM_KEYS.festivalMonth)) || null
  };
}
function setOrDelete(params: URLSearchParams, key: string, value: string | undefined) {
  if (value) params.set(key, value);else params.delete(key);
}
function write(params: URLSearchParams, state: IntegratedSearchFilterState) {
  setOrDelete(params, PARAM_KEYS.regionCode, state.regionCode ?? undefined);
  setOrDelete(params, PARAM_KEYS.subRegionCode, state.subRegionCode ?? undefined);
  setOrDelete(params, PARAM_KEYS.policyInfoProvisionCodes, state.policyInfoProvisionCodes.length ? state.policyInfoProvisionCodes.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.giftCategoryIds, state.giftCategoryIds.length ? state.giftCategoryIds.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.giftInstitutionIds, state.giftInstitutionIds.length ? state.giftInstitutionIds.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.donationInstitutionIds, state.donationInstitutionIds.length ? state.donationInstitutionIds.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.donationSort, state.donationSort !== DEFAULTS.donationSort ? state.donationSort : undefined);
  setOrDelete(params, PARAM_KEYS.festivalLclsSystm2, state.festivalLclsSystm2 ?? undefined);
  setOrDelete(params, PARAM_KEYS.festivalRegionCode, state.festivalRegionCode ?? undefined);
  setOrDelete(params, PARAM_KEYS.festivalSubRegionCode, state.festivalSubRegionCode ?? undefined);
  setOrDelete(params, PARAM_KEYS.festivalRadius, state.festivalRadius !== DEFAULTS.festivalRadius ? String(state.festivalRadius) : undefined);
  setOrDelete(params, PARAM_KEYS.festivalMonth, state.festivalMonth ? String(state.festivalMonth) : undefined);
  // 통합 카테고리 필터 변경은 항상 첫 페이지로 리셋
  params.delete(PARAM_KEYS.page);
}
export function useIntegratedSearchFilter() {
  const $ = _c(74);
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.toString() ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const paramsString = t0;
  let t1;
  let t2;
  if ($[2] !== paramsString) {
    t1 = () => parse(new URLSearchParams(paramsString));
    t2 = [paramsString];
    $[2] = paramsString;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const [state, setState] = useSubscribedState(t1, t2);
  const pendingRef = useRef(null);
  let t3;
  if ($[5] !== paramsString || $[6] !== pathname || $[7] !== router) {
    t3 = () => {
      const pending = pendingRef.current;
      if (!pending) {
        return;
      }
      pendingRef.current = null;
      const params = new URLSearchParams(paramsString);
      write(params, pending);
      const qs = params.toString();
      const url = qs ? `${pathname}?${qs}` : pathname;
      router.replace(url, {
        scroll: false
      });
    };
    $[5] = paramsString;
    $[6] = pathname;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== paramsString || $[10] !== pathname || $[11] !== router || $[12] !== state) {
    t4 = [state, pathname, paramsString, router];
    $[9] = paramsString;
    $[10] = pathname;
    $[11] = router;
    $[12] = state;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== setState) {
    t5 = updater => {
      setState(prev => {
        const next = updater(prev);
        pendingRef.current = next;
        return next;
      });
    };
    $[14] = setState;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const update = t5;
  let t6;
  if ($[16] !== update) {
    t6 = code => {
      update(prev_0 => prev_0.regionCode === code ? prev_0 : {
        ...prev_0,
        regionCode: code,
        subRegionCode: null
      });
    };
    $[16] = update;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const setRegionCode = t6;
  let t7;
  if ($[18] !== update) {
    t7 = code_0 => {
      update(prev_1 => ({
        ...prev_1,
        subRegionCode: code_0
      }));
    };
    $[18] = update;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const setSubRegionCode = t7;
  let t8;
  if ($[20] !== update) {
    t8 = code_1 => {
      update(prev_2 => ({
        ...prev_2,
        policyInfoProvisionCodes: toggleArrayItem(prev_2.policyInfoProvisionCodes, code_1)
      }));
    };
    $[20] = update;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const togglePolicyInfoProvision = t8;
  let t9;
  if ($[22] !== update) {
    t9 = id => {
      update(prev_3 => ({
        ...prev_3,
        giftCategoryIds: toggleArrayItem(prev_3.giftCategoryIds, id)
      }));
    };
    $[22] = update;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const toggleGiftCategory = t9;
  let t10;
  if ($[24] !== update) {
    t10 = id_0 => {
      update(prev_4 => ({
        ...prev_4,
        giftInstitutionIds: toggleArrayItem(prev_4.giftInstitutionIds, id_0)
      }));
    };
    $[24] = update;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const toggleGiftInstitution = t10;
  let t11;
  if ($[26] !== update) {
    t11 = id_1 => {
      update(prev_5 => ({
        ...prev_5,
        donationInstitutionIds: toggleArrayItem(prev_5.donationInstitutionIds, id_1)
      }));
    };
    $[26] = update;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const toggleDonationInstitution = t11;
  let t12;
  if ($[28] !== update) {
    t12 = sort => {
      update(prev_6 => ({
        ...prev_6,
        donationSort: sort
      }));
    };
    $[28] = update;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const setDonationSort = t12;
  let t13;
  if ($[30] !== update) {
    t13 = code_2 => {
      update(prev_7 => ({
        ...prev_7,
        festivalLclsSystm2: code_2
      }));
    };
    $[30] = update;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const setFestivalLclsSystm2 = t13;
  let t14;
  if ($[32] !== update) {
    t14 = code_3 => {
      update(prev_8 => prev_8.festivalRegionCode === code_3 ? prev_8 : {
        ...prev_8,
        festivalRegionCode: code_3,
        festivalSubRegionCode: null
      });
    };
    $[32] = update;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const setFestivalRegionCode = t14;
  let t15;
  if ($[34] !== update) {
    t15 = code_4 => {
      update(prev_9 => ({
        ...prev_9,
        festivalSubRegionCode: code_4
      }));
    };
    $[34] = update;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const setFestivalSubRegionCode = t15;
  let t16;
  if ($[36] !== update) {
    t16 = radius => {
      update(prev_10 => ({
        ...prev_10,
        festivalRadius: radius ?? DEFAULTS.festivalRadius
      }));
    };
    $[36] = update;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const setFestivalRadius = t16;
  let t17;
  if ($[38] !== update) {
    t17 = month => {
      update(prev_11 => ({
        ...prev_11,
        festivalMonth: month
      }));
    };
    $[38] = update;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const setFestivalMonth = t17;
  let t18;
  if ($[40] !== update) {
    t18 = () => {
      update(_temp);
    };
    $[40] = update;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const resetHometownNewsFilters = t18;
  let t19;
  if ($[42] !== update) {
    t19 = () => {
      update(_temp2);
    };
    $[42] = update;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const resetPolicyInfoFilters = t19;
  let t20;
  if ($[44] !== update) {
    t20 = () => {
      update(_temp3);
    };
    $[44] = update;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const resetGiftFilters = t20;
  let t21;
  if ($[46] !== update) {
    t21 = () => {
      update(_temp4);
    };
    $[46] = update;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  const resetDonationFilters = t21;
  let t22;
  if ($[48] !== update) {
    t22 = () => {
      update(_temp5);
    };
    $[48] = update;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const resetFestivalFilters = t22;
  const hometownNewsFilterCount = (state.regionCode ? 1 : 0) + (state.subRegionCode ? 1 : 0);
  const policyInfoFilterCount = state.policyInfoProvisionCodes.length;
  const giftFilterCount = state.giftCategoryIds.length + state.giftInstitutionIds.length;
  const donationFilterCount = state.donationInstitutionIds.length;
  const festivalFilterCount = (state.festivalLclsSystm2 ? 1 : 0) + (state.festivalSubRegionCode ? 1 : 0) + (state.festivalMonth ? 1 : 0);
  let t23;
  if ($[50] !== donationFilterCount || $[51] !== festivalFilterCount || $[52] !== giftFilterCount || $[53] !== hometownNewsFilterCount || $[54] !== policyInfoFilterCount || $[55] !== resetDonationFilters || $[56] !== resetFestivalFilters || $[57] !== resetGiftFilters || $[58] !== resetHometownNewsFilters || $[59] !== resetPolicyInfoFilters || $[60] !== setDonationSort || $[61] !== setFestivalLclsSystm2 || $[62] !== setFestivalMonth || $[63] !== setFestivalRadius || $[64] !== setFestivalRegionCode || $[65] !== setFestivalSubRegionCode || $[66] !== setRegionCode || $[67] !== setSubRegionCode || $[68] !== state || $[69] !== toggleDonationInstitution || $[70] !== toggleGiftCategory || $[71] !== toggleGiftInstitution || $[72] !== togglePolicyInfoProvision) {
    t23 = {
      state,
      setRegionCode,
      setSubRegionCode,
      togglePolicyInfoProvision,
      toggleGiftCategory,
      toggleGiftInstitution,
      toggleDonationInstitution,
      setDonationSort,
      setFestivalLclsSystm2,
      setFestivalRegionCode,
      setFestivalSubRegionCode,
      setFestivalRadius,
      setFestivalMonth,
      resetHometownNewsFilters,
      resetPolicyInfoFilters,
      resetGiftFilters,
      resetDonationFilters,
      resetFestivalFilters,
      hometownNewsFilterCount,
      policyInfoFilterCount,
      giftFilterCount,
      donationFilterCount,
      festivalFilterCount
    };
    $[50] = donationFilterCount;
    $[51] = festivalFilterCount;
    $[52] = giftFilterCount;
    $[53] = hometownNewsFilterCount;
    $[54] = policyInfoFilterCount;
    $[55] = resetDonationFilters;
    $[56] = resetFestivalFilters;
    $[57] = resetGiftFilters;
    $[58] = resetHometownNewsFilters;
    $[59] = resetPolicyInfoFilters;
    $[60] = setDonationSort;
    $[61] = setFestivalLclsSystm2;
    $[62] = setFestivalMonth;
    $[63] = setFestivalRadius;
    $[64] = setFestivalRegionCode;
    $[65] = setFestivalSubRegionCode;
    $[66] = setRegionCode;
    $[67] = setSubRegionCode;
    $[68] = state;
    $[69] = toggleDonationInstitution;
    $[70] = toggleGiftCategory;
    $[71] = toggleGiftInstitution;
    $[72] = togglePolicyInfoProvision;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  return t23;
}
function _temp5(prev_16) {
  return {
    ...prev_16,
    festivalLclsSystm2: null,
    festivalRegionCode: null,
    festivalSubRegionCode: null,
    festivalRadius: DEFAULTS.festivalRadius,
    festivalMonth: null
  };
}
function _temp4(prev_15) {
  return {
    ...prev_15,
    donationInstitutionIds: []
  };
}
function _temp3(prev_14) {
  return {
    ...prev_14,
    giftCategoryIds: [],
    giftInstitutionIds: []
  };
}
function _temp2(prev_13) {
  return {
    ...prev_13,
    policyInfoProvisionCodes: []
  };
}
function _temp(prev_12) {
  return {
    ...prev_12,
    regionCode: null,
    subRegionCode: null
  };
}